/**
 * Library Lookup — filtered view of SCAN + ISBN books
 *
 * Simple list for admin to work through at their local library's website.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, ExternalLink, Loader2, BookOpen } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { glassStyles } from '@/lib/design';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bookstoreTable = () => (supabase as any).from('bookstore_books');

interface LookupBook {
  id: string;
  title: string;
  author: string | null;
  isbn: string;
  source_url: string;
  cover_image_url: string | null;
}

export function LibraryLookup() {
  const [books, setBooks] = useState<LookupBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchErr } = await bookstoreTable()
        .select('id, title, author, isbn, source_url, cover_image_url')
        .eq('status', 'scan')
        .not('isbn', 'is', null)
        .order('title');

      if (fetchErr) throw new Error(fetchErr.message);
      setBooks(data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load books');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  const filtered = useMemo(() => {
    if (!searchTerm) return books;
    const q = searchTerm.toLowerCase();
    return books.filter(b =>
      b.title.toLowerCase().includes(q) ||
      (b.author && b.author.toLowerCase().includes(q)) ||
      b.isbn.includes(q)
    );
  }, [books, searchTerm]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#1A1A1A] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl p-8 text-center" style={glassStyles.card}>
        <p className="text-[#FF3B30] text-lg font-semibold mb-2">Error</p>
        <p className="text-[#8E8E93] text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[#1C1C1E]">Library Lookup</h2>
          <p className="text-sm text-[#8E8E93] mt-0.5">
            {books.length} book{books.length !== 1 ? 's' : ''} ready to find at your library
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative md:max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" />
        <input
          type="text"
          placeholder="Search title, author, ISBN..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-black/[0.08] rounded-xl text-[#1C1C1E] text-sm placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A] focus:ring-1 focus:ring-[#1A1A1A]/20"
        />
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="rounded-2xl p-8 text-center" style={glassStyles.card}>
          <BookOpen className="w-10 h-10 text-[#8E8E93] mx-auto mb-3" />
          <p className="text-[#8E8E93] text-sm">
            {books.length === 0
              ? 'No books ready for library lookup yet. Mark books as SCAN and add their ISBN in the Bookstore Books tab.'
              : 'No books match your search.'}
          </p>
        </div>
      )}

      {/* Book list */}
      {filtered.length > 0 && (
        <div className="rounded-2xl overflow-hidden" style={glassStyles.card}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-black/[0.06] text-left text-xs text-[#8E8E93] uppercase tracking-wider">
                <th className="px-4 py-3">Book</th>
                <th className="px-4 py-3">ISBN</th>
                <th className="px-4 py-3 text-right">Link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/[0.04]">
              {filtered.map(book => (
                <tr key={book.id} className="hover:bg-black/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {book.cover_image_url && (
                        <img src={book.cover_image_url} alt="" className="w-8 h-12 object-cover rounded bg-black/[0.03]" />
                      )}
                      <div>
                        <p className="font-medium text-sm text-[#1C1C1E]">{book.title}</p>
                        {book.author && <p className="text-xs text-[#8E8E93]">{book.author}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-[#1C1C1E]">{book.isbn}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={book.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#1A1A1A] hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default LibraryLookup;
